import { useEffect, useMemo, useState } from 'react'
import { db } from '../firebase/config'
import { client } from './Client'
import notDeleted from '../helpers/notDeleted'

// Per-session cache keyed by client + scope + personaId. Values are Promises
// (in-flight OR resolved) so that N cards mounting concurrently all await
// the SAME request instead of each firing its own duplicate query.
// Response counts don't change often enough to warrant live listeners.
const cache = new Map()

const chunk = (arr, size) => {
    const out = []
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size))
    return out
}

const fetchCountForFields = async (fieldIds, personaId) => {
    if (!fieldIds.length) return 0
    // Firestore 'in' queries cap at 10 elements on the v8 SDK.
    const chunks = chunk(fieldIds, 10)
    const snaps = await Promise.all(chunks.map(c =>
        db.collection('QuestionnairesResponses')
            .where('CompagnyID', '==', client)
            .where('Persona', '==', personaId)
            .where('FieldID', 'in', c)
            .get()
    ))
    // snap.size telt ook verwijderde antwoorden mee: deze read gaat buiten de
    // hooks om, dus het centrale leesfilter komt er niet aan te pas.
    // AI-CONTEXT: See docs/pitfalls.md#database-missing-compagnyid-on-fresh-fetch
    return snaps.reduce((sum, s) => sum + s.docs.filter((d) => notDeleted(d.data())).length, 0)
}

const fetchCountForResearch = async (researchId, personaId) => {
    const snap = await db.collection('QuestionnairesResponses')
        .where('CompagnyID', '==', client)
        .where('Persona', '==', personaId)
        .where('ResearchID', '==', researchId)
        .get()
    // Zie hierboven: buiten de hooks om filtert niets de verwijderde antwoorden.
    return snap.docs.filter((d) => notDeleted(d.data())).length
}

const usePersonaResponseCounts = ({ personas, fieldIds, researchId } = {}) => {
    const [counts, setCounts] = useState({})

    const personasKey = useMemo(
        () => (Array.isArray(personas) ? personas.map(p => p.ID).sort().join(',') : ''),
        [personas]
    )

    const fieldIdsKey = useMemo(
        () => (Array.isArray(fieldIds) ? [...fieldIds].filter(Boolean).sort().join(',') : ''),
        [fieldIds]
    )

    useEffect(() => {
        if (!personasKey) { setCounts({}); return }
        if (!researchId && !fieldIdsKey) { setCounts({}); return }

        const personaIds = personasKey.split(',')
        const scope = researchId ? `research:${researchId}` : `fields:${fieldIdsKey}`

        const fetchOne = (personaId) => {
            const cacheKey = `${client}:${scope}:${personaId}`
            if (cache.has(cacheKey)) return cache.get(cacheKey)
            const promise = researchId
                ? fetchCountForResearch(researchId, personaId)
                : fetchCountForFields(fieldIdsKey.split(','), personaId)
            cache.set(cacheKey, promise)
            promise.catch(() => cache.delete(cacheKey))
            return promise
        }

        let cancelled = false
        Promise.all(personaIds.map(id => fetchOne(id).then(n => [id, n])))
            .then(pairs => { if (!cancelled) setCounts(Object.fromEntries(pairs)) })

        return () => { cancelled = true }
    }, [personasKey, fieldIdsKey, researchId])

    return counts
}

export default usePersonaResponseCounts
